<template>
  <div class="pickable-buttons">
    <button
      v-for="option in options"
      :key="option[trackOptionsBy]"
      class="pickable-buttons-option"
      :class="{
        'pickable-buttons-option-selected': selectedOption?.[trackOptionsBy] &&selectedOption?.[trackOptionsBy] === option[trackOptionsBy],
        'pickable-buttons-option-disabled': option.disabled
      }"
      :disabled="option.disabled"
      @click="handleOptionClick(option)"
    >
      <span v-if="option.badge" class="pickable-buttons-option-badge">
        {{ option.badge }}

        <span class="pickable-buttons-option-badge-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </span>

      <div class="pickable-buttons-option-label">
        {{ option.label }}
      </div>

      <div class="pickable-buttons-option-description">
        {{ option.description }}
      </div>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PickableButtons',
  props: {
    value: {
      type: undefined,
      default: undefined
    },
    options: {
      type: Array,
      required: true
    },
    trackOptionsBy: {
      type: [String, Number],
      default: 'label'
    }
  },
  data() {
    return {
      selectedOption: this.value
    }
  },
  methods: {
    handleOptionClick(option) {
      /** @note The stylesheets reset native button styles, so the `disabled` attribute alone does not block the click. */
      if (option.disabled) return

      this.selectedOption = option

      this.$emit('input', this.selectedOption)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "@/../static/stylesheets/variables" as *;

.pickable-buttons {
  display: flex;
  gap: 1rem;

  &-option {
    position: relative;
    display: flex;
    flex-direction: column;
    user-select: none;
    cursor: pointer;
    background-color: transparent;
    margin: 0;
    border: 1px solid #CCCCCC;
    border-radius: 0.25rem;
    padding: 1rem;
    width: 14rem;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    /** @note Necessary rule to reset some unnecessary inherited styles (Due to the usage of style tag without scoped). */
    height: auto;
    font-size: medium;
    font-weight: normal;
    transition: none;
    animation-duration: initial;
    color: initial;

    &-selected {
      border-color: $theme-primary-color;
    }
  }

  &-option-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: $theme-primary-color;
    text-transform: uppercase;
  }

  &-option-badge {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    transform: translate(-50%, -50%);
    background-color: $theme-primary-color;
    color: #FFFFFF;
    border-radius: 62.5rem;
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.2);
    padding: 0.375rem 0.875rem;
    font-size: 0.75rem;
    font-weight: bold;
    white-space: nowrap;
  }

  &-option-badge-dots {
    display: inline-flex;
    align-self: flex-end;
    align-items: center;
    gap: 0.1875rem;
    margin-bottom: 0.1875rem;

    span {
      width: 0.3125rem;
      height: 0.3125rem;
      border-radius: 50%;
      background-color: currentColor;
      animation: pickable-buttons-badge-dot 2.4s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.3s;
      }

      &:nth-child(3) {
        animation-delay: 0.6s;
      }
    }
  }

  &-option-description {
    font-size: 0.875rem;
    font-weight: 500;
    color: #999999;
  }

  /** @note Declared after `&-option` to override the `-selected` border color at the same specificity. */
  &-option-disabled {
    cursor: not-allowed;
    border-style: dashed;
    border-color: #CCCCCC;
    background-color: #F5F5F5;

    .pickable-buttons-option-label,
    .pickable-buttons-option-description {
      opacity: 0.3;
    }
  }
}

@keyframes pickable-buttons-badge-dot {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.7);
  }

  50% {
    opacity: 1;
    transform: scale(1.9);
  }
}
</style>
